import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { DocumentUploader } from "../components/documents/DocumentUploader";
import { FileCard } from "../components/documents/FileCard";
import { ShareDocumentModal } from "../components/documents/ShareDocumentModal";
import { Header } from "../components/Header";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { getAllDocuments } from "../services/documentService";
import type { Document, DocumentCategory } from "../types/document";
import styles from "./FilesPage.module.css";

const CATEGORY_ORDER: DocumentCategory[] = [
	"lab_results",
	"prescriptions",
	"imaging",
	"medical_records",
	"insurance",
	"other",
];

function LoadingSkeleton() {
	return (
		<div className={styles.skeletonContainer} aria-busy="true">
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
		</div>
	);
}

function EmptyState({
	onUpload,
	t,
}: {
	onUpload: () => void;
	t: (key: string) => string;
}) {
	return (
		<div className={styles.emptyState}>
			<img
				src="/images/home/home-top-nav/files-icon.svg"
				alt=""
				className={styles.emptyIcon}
			/>
			<h2 className={styles.emptyTitle}>{t("empty.title")}</h2>
			<p className={styles.emptyDescription}>{t("empty.description")}</p>
			<button type="button" className={styles.emptyCta} onClick={onUpload}>
				{t("empty.cta")}
			</button>
		</div>
	);
}

function ErrorState({
	onRetry,
	t,
}: {
	onRetry: () => void;
	t: (key: string) => string;
}) {
	return (
		<div className={styles.errorState} role="alert">
			<i className={`pi pi-exclamation-circle ${styles.errorIcon}`} />
			<h2 className={styles.errorTitle}>{t("error.title")}</h2>
			<p className={styles.errorDescription}>{t("error.description")}</p>
			<button type="button" className={styles.errorRetry} onClick={onRetry}>
				{t("error.retry")}
			</button>
		</div>
	);
}

function NoResults({ t }: { t: (key: string) => string }) {
	return <p className={styles.noResults}>{t("search.noResults")}</p>;
}

export function FilesPage() {
	const { t } = useClientTranslations("files");
	const { logEvent } = useAnalytics();
	const queryClient = useQueryClient();

	const [searchTerm, setSearchTerm] = useState("");
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logEvent("files_page_viewed");
		}
	}, [logEvent]);

	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["documents"],
		queryFn: () => getAllDocuments(),
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
	});

	const filteredDocuments = useMemo(() => {
		if (!data?.documents) return [];
		if (!searchTerm.trim()) return data.documents;
		const lower = searchTerm.toLowerCase();
		return data.documents.filter((doc) =>
			doc.name.toLowerCase().includes(lower),
		);
	}, [data?.documents, searchTerm]);

	const groupedDocuments = useMemo(() => {
		const groups = new Map<DocumentCategory, Document[]>();
		for (const doc of filteredDocuments) {
			const cat = CATEGORY_ORDER.includes(doc.category)
				? doc.category
				: "other";
			const existing = groups.get(cat) || [];
			existing.push(doc);
			groups.set(cat, existing);
		}
		return CATEGORY_ORDER.filter((cat) => groups.has(cat)).map((cat) => ({
			category: cat,
			documents: groups.get(cat) as Document[],
		}));
	}, [filteredDocuments]);

	const [shareDoc, setShareDocument] = useState<Document | null>(null);

	const handleShare = useCallback((doc: Document) => {
		setShareDocument(doc);
	}, []);

	const handleUploadComplete = (_document: Document) => {
		queryClient.invalidateQueries({ queryKey: ["documents"] });
		setIsUploadModalOpen(false);
		toast.success(t("upload.success", { ns: "documents" }));
	};

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{/* PageHeader: icon + title + subtitle */}
				<div className={styles.pageHeader}>
					<img
						src="/images/home/insights/files-insights.png"
						alt=""
						className={styles.pageIcon}
					/>
					<div className={styles.pageHeaderText}>
						<h1 className={styles.pageTitle}>{t("page.title")}</h1>
						<p className={styles.pageSubtitle}>{t("page.subtitle")}</p>
					</div>
				</div>

				{/* SearchRow: search input + upload button */}
				<div className={styles.searchRow}>
					<div className={styles.searchBar}>
						<i className="pi pi-search" />
						<input
							type="search"
							className={styles.searchInput}
							placeholder={t("search.placeholder")}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							aria-label={t("search.placeholder")}
						/>
						{searchTerm && (
							<button
								type="button"
								className={styles.clearButton}
								onClick={() => setSearchTerm("")}
								aria-label="Clear search"
							>
								<i className="pi pi-times" />
							</button>
						)}
					</div>
					<button
						type="button"
						className={styles.uploadButton}
						onClick={() => setIsUploadModalOpen(true)}
						aria-label={t("upload.button")}
					>
						<i className="pi pi-plus" />
					</button>
				</div>

				{/* Content area: loading / error / empty / no-results / documents list */}
				{isLoading && <LoadingSkeleton />}
				{isError && <ErrorState onRetry={() => refetch()} t={t} />}
				{!isLoading &&
					!isError &&
					filteredDocuments.length === 0 &&
					searchTerm.trim() && <NoResults t={t} />}
				{!isLoading &&
					!isError &&
					data?.documents?.length === 0 &&
					!searchTerm.trim() && (
						<EmptyState onUpload={() => setIsUploadModalOpen(true)} t={t} />
					)}
				{!isLoading && !isError && filteredDocuments.length > 0 && (
					<div className={styles.categoriesList}>
						{groupedDocuments.map(({ category, documents }) => (
							<section key={category} className={styles.categorySection}>
								<div className={styles.categoryHeader}>
									<h2 className={styles.categoryTitle}>
										{t(`categories.${category}.title`)}
									</h2>
									<p className={styles.categorySubtitle}>
										{t(`categories.${category}.subtitle`)}
									</p>
								</div>
								<div className={styles.categoryCards}>
									{documents.map((doc) => (
										<FileCard
											key={doc.id}
											document={doc}
											t={t}
											onShare={handleShare}
										/>
									))}
								</div>
							</section>
						))}
					</div>
				)}
			</main>

			{/* Upload Modal: Headless UI Dialog wrapping DocumentUploader */}
			<Transition appear show={isUploadModalOpen} as={Fragment}>
				<Dialog
					as="div"
					className="relative"
					onClose={() => !isUploading && setIsUploadModalOpen(false)}
				>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className={styles.modalOverlay} />
					</Transition.Child>
					<div className={styles.modalContainer}>
						<div className={styles.modalCentered}>
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								<Dialog.Panel className={styles.modalPanel}>
									<div className={styles.modalHeader}>
										<Dialog.Title className={styles.modalTitle}>
											{t("upload.modalTitle")}
										</Dialog.Title>
										<button
											type="button"
											className={styles.modalCloseButton}
											onClick={() =>
												!isUploading && setIsUploadModalOpen(false)
											}
										>
											<XMarkIcon className={styles.modalCloseIcon} />
										</button>
									</div>
									<DocumentUploader
										onUploadComplete={handleUploadComplete}
										isUploading={isUploading}
										setIsUploading={setIsUploading}
									/>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition>

			{/* Share Modal */}
			<ShareDocumentModal
				document={shareDoc}
				isOpen={shareDoc !== null}
				onClose={() => setShareDocument(null)}
				t={t}
			/>
		</div>
	);
}
